import React, {useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {Box, Button, HStack, Text, VStack} from '@chakra-ui/react3'
import BVProductRating from '../../../../components/bazaarvoice/rating'
import {hasBVProductRating} from '../../../../components/bazaarvoice/utils'
import ProductDisplayPrice from '../../../../components/product-display-price'
import useAR from '../../../../hooks/use-ar'
import {useConfiguratorHighlights} from '../../../../hooks/useScapiCustomApi'
import {_3DViewIcon, ResourcesAddIcon, TextCircleIcon} from '../../../../leap/components/icons'
import {findMainSetProduct, getMasterProductId, isProductSet} from '../../../../utils/product-utils'
import BadgeGroup from '../../../../components/badge-group'
import ProductAvailability from '../../../../leap/components/product-availability/index.jsx'
import PromotionBanner from '../../../../leap/components/promotion-banner'
import Gallery from './gallery'
import DisplayPrice from '../../../../leap/components/display-price/index.jsx'
import AgeRange from '../../../../leap/components/age-range'
import CockpitCtaSection from './cockpit-cta-section'
import EstimatedDelivery from '../../../../leap/components/estimated-delivery'
import ColorSwatches from '../../../../leap/components/color-swatches'
import logger from '../../../../utils/logger-instance'

const HighlightItem = ({children}) => (
    <HStack
        gap="spacing-12"
        align="center"
        color="text.primary"
        textStyle="heading.small"
        _icon={{width: '1rem', height: '1rem'}}
    >
        {children}
    </HStack>
)
HighlightItem.propTypes = {
    children: PropTypes.node
}

const SetProductSwatches = ({product}) => {
    return <ColorSwatches.Root asChild product={product} />
}
SetProductSwatches.propTypes = {
    product: PropTypes.object
}

const XleapCockpit = ({
    derivedProduct,
    product,
    isGetPropsLoading,
    isMasterLoading,
    isPromotionsLoading,
    isVariantLoading,
    onRatingsClick,
    viewModel
}) => {
    const {formatMessage} = useIntl()
    const configuratorConfig = product?.c_configuratorConfig
    const displayTemplate = product?.c_displayTemplate
    const hasProductRating = useMemo(() => hasBVProductRating(product), [product])
    const {ARModel} = derivedProduct
    const ARProps = useAR(ARModel)
    const highlightsQuery = useConfiguratorHighlights(
        {
            parameters: {
                configId: configuratorConfig,
                productId: getMasterProductId(product) || product?.id
            }
        },
        {
            enabled: displayTemplate === 'xpdp-configurator'
        }
    )
    const hasHighlights =
        highlightsQuery.data?.numberOfColors > 0 ||
        highlightsQuery.data?.numberOfAccessories > 0 ||
        highlightsQuery.data?.hasEngraving

    useEffect(() => {
        if (displayTemplate === 'xpdp-configurator' && !product?.c_configuratorConfigItemId) {
            logger.error(`"${product.id}" has no colorProduct configured in configurator config`)
        }
    }, [displayTemplate, product?.c_configuratorConfigItemId, product?.id])

    const mainSetProduct = useMemo(
        () =>
            displayTemplate === 'xpdp-configurator' &&
            (isProductSet(product) ? findMainSetProduct(product) : product),
        [product, displayTemplate]
    )

    return (
        <Box paddingInline={{base: 0, s: 'spacing-92'}}>
            <Box
                data-testid={`cockpit-${displayTemplate}`}
                display={{
                    base: 'flex',
                    s: 'grid'
                }}
                flexDirection="column"
                paddingTop={{base: 'spacing-16', s: 'spacing-32'}}
                paddingBottom={{base: 0, s: 'spacing-64'}}
                gapX={{base: 'spacing-32', s: 'spacing-80'}}
                align={{base: 'stretch', s: 'start'}}
                layerStyle="container.page"
                gridTemplateAreas='"gallery details"'
                gridTemplateColumns="2fr 1fr"
                gridTemplateRows="auto auto"
            >
                <Box
                    gridArea="gallery"
                    position="sticky"
                    top={{base: 'spacing-8', s: 'spacing-32'}}
                    paddingTop="spacing-8"
                    paddingBottom="spacing-24"
                    zIndex="sticky"
                    alignSelf={{base: 'stretch', s: 'start'}}
                    height="fit-content"
                    bg={{base: 'bg', s: 'transparent'}}
                    role="group"
                    // This pseudoelement is used to hide the extra space above the gallery introduced by the value of the top position property
                    _before={{
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: {base: '-spacing-8', s: '-spacing-32'},
                        height: {base: 'spacing-8', s: 'spacing-32'},
                        backgroundColor: 'bg',
                        pointerEvents: 'none'
                    }}
                    aria-label={formatMessage(
                        {
                            id: 'product_detail.product_name_carousel',
                            defaultMessage: '{productName} carousel'
                        },
                        {productName: product.name}
                    )}
                >
                    <Gallery
                        product={product}
                        derivedProduct={derivedProduct}
                        isMasterLoading={isMasterLoading}
                        isVariantLoading={isVariantLoading}
                    />
                </Box>

                <Box gridArea="details" minWidth={0}>
                    <VStack gap="spacing-32" align="stretch">
                        <Box data-testid="main-info" paddingInline={{base: 'spacing-24', s: 0}}>
                            <VStack gap={{base: 'spacing-20', s: 'spacing-24'}} align="stretch">
                                <VStack gap="spacing-4" align="stretch">
                                    <Box>
                                        <Box float="right">
                                            <BadgeGroup product={product} />
                                        </Box>
                                        <Box>
                                            <AgeRange
                                                product={product}
                                                variant="configurator-quickview"
                                            />
                                        </Box>
                                        <Text
                                            as="h1"
                                            paddingTop={{base: 'spacing-20', s: 'spacing-24'}}
                                        >
                                            <Text
                                                as="span"
                                                display="inline-block"
                                                color="text.primary"
                                                textStyle="display.small"
                                            >
                                                {product.name}
                                            </Text>
                                        </Text>
                                    </Box>

                                    <Text
                                        as="span"
                                        display="inline-block"
                                        color="text.tertiary"
                                        textStyle="body.medium"
                                    >
                                        {product.c_slogan}
                                    </Text>
                                </VStack>

                                {hasProductRating && (
                                    <BVProductRating
                                        product={product}
                                        variant="cockpit"
                                        onRatingsClick={onRatingsClick}
                                    />
                                )}
                                <PromotionBanner product={product} />
                            </VStack>
                        </Box>

                        <VStack
                            gap="spacing-24"
                            align="stretch"
                            paddingInline={{base: 'spacing-24', s: 0}}
                        >
                            {!isProductSet(product) ? (
                                <SetProductSwatches product={product} />
                            ) : displayTemplate === 'xpdp' ? (
                                product.setProducts.map((setProduct) => (
                                    <SetProductSwatches
                                        key={setProduct.setProductId}
                                        product={setProduct}
                                    />
                                ))
                            ) : displayTemplate === 'xpdp-configurator' && mainSetProduct ? (
                                <SetProductSwatches product={mainSetProduct} />
                            ) : null}

                            <Text color="text.secondary" textStyle="body.small">
                                {product.shortDescription}
                            </Text>

                            {displayTemplate === 'xpdp-configurator' && hasHighlights && (
                                <VStack gap="spacing-12" align="stretch">
                                    {highlightsQuery.data?.numberOfAccessories > 0 && (
                                        <HighlightItem>
                                            <ResourcesAddIcon role="presentation" />

                                            <FormattedMessage
                                                id="product_detail.number_of_accessories"
                                                defaultMessage="{value} accessories available"
                                                values={{
                                                    value: highlightsQuery.data?.numberOfAccessories
                                                }}
                                            />
                                        </HighlightItem>
                                    )}

                                    {highlightsQuery.data?.hasEngraving && (
                                        <HighlightItem>
                                            <TextCircleIcon role="presentation" />

                                            <FormattedMessage
                                                id="product_detail.has_engraving"
                                                defaultMessage="Free engraving"
                                            />
                                        </HighlightItem>
                                    )}
                                </VStack>
                            )}

                            {ARProps?.hasAR && (
                                <Button
                                    as="a"
                                    variant="secondaryOutline"
                                    display={{base: 'inline-flex', s: 'none'}}
                                    href={ARProps?.href || '#'}
                                    rel="ar"
                                    onClick={ARProps?.onARLinkClick}
                                    aria-label={formatMessage({
                                        defaultMessage: 'Open AR view',
                                        id: 'product_detail.button.open_ar_view'
                                    })}
                                >
                                    <span>
                                        <FormattedMessage
                                            id="product_detail.button.view_in_space"
                                            defaultMessage="View in space"
                                        />
                                    </span>
                                    <_3DViewIcon role="presentation" />
                                </Button>
                            )}
                        </VStack>

                        <VStack
                            gap="spacing-16"
                            align="stretch"
                            paddingInline={{base: 'spacing-24', s: 0}}
                            paddingBlock="spacing-24"
                            backgroundColor="primary.base.white"
                            borderTop="1px solid"
                            borderTopColor="border.subtle"
                            backdropFilter="auto"
                            backdropBlur="md"
                        >
                            <HStack justify="space-between" align="flex-end">
                                <ProductDisplayPrice
                                    as={DisplayPrice}
                                    product={product}
                                    loading={
                                        isGetPropsLoading || isPromotionsLoading || isVariantLoading
                                    }
                                    showVatLabel={true}
                                />
                                {!isProductSet(product) && (
                                    <ProductAvailability
                                        loading={isVariantLoading}
                                        flexShrink="0"
                                        product={product}
                                    />
                                )}
                            </HStack>
                            <CockpitCtaSection viewModel={viewModel} />
                            <EstimatedDelivery product={product}></EstimatedDelivery>
                        </VStack>
                    </VStack>
                </Box>
            </Box>
        </Box>
    )
}

XleapCockpit.propTypes = {
    derivedProduct: PropTypes.object,
    product: PropTypes.object,
    isGetPropsLoading: PropTypes.bool,
    isMasterLoading: PropTypes.bool,
    isPromotionsLoading: PropTypes.bool,
    isVariantLoading: PropTypes.bool,
    onRatingsClick: PropTypes.func,
    viewModel: PropTypes.object.isRequired
}

export default XleapCockpit
